import React from 'react';
import { useTheme } from '../core/ui/ThemeContext';

const LiquidGlassButton = ({
  children,
  onClick,
  disabled = false,
  variant = 'primary',
  size = 'medium',
  loading = false,
  className = '',
  title,
  icon: Icon,
  iconPosition = 'left',
  ...props
}) => {
  const { isDarkMode } = useTheme();

  // Variant styles - optimiert für Light/Dark Mode
  const variants = {
    primary: {
      gradient: isDarkMode 
        ? 'from-blue-600/60 via-blue-700/60 to-blue-800/60' 
        : 'from-blue-500/80 via-blue-600/80 to-blue-700/80',
      shadow: isDarkMode
        ? 'shadow-[0_8px_32px_rgba(59,130,246,0.4)] group-hover/btn:shadow-[0_12px_40px_rgba(59,130,246,0.6)]'
        : 'shadow-[0_4px_20px_rgba(59,130,246,0.25)] group-hover/btn:shadow-[0_8px_32px_rgba(59,130,246,0.4)]',
      hoverColor: 'text-blue-300',
      border: isDarkMode ? 'border-white/30' : 'border-blue-200/50'
    },
    secondary: {
      gradient: isDarkMode 
        ? 'from-slate-600/60 via-gray-600/60 to-slate-700/60' 
        : 'from-gray-400/70 via-gray-500/70 to-gray-600/70',
      shadow: isDarkMode
        ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)] group-hover/btn:shadow-[0_12px_40px_rgba(0,0,0,0.6)]'
        : 'shadow-[0_4px_20px_rgba(0,0,0,0.15)] group-hover/btn:shadow-[0_8px_32px_rgba(0,0,0,0.25)]',
      hoverColor: 'text-gray-300',
      border: isDarkMode ? 'border-white/30' : 'border-gray-300/50'
    },
    success: {
      gradient: isDarkMode 
        ? 'from-emerald-600/60 via-green-600/60 to-emerald-700/60' 
        : 'from-emerald-500/80 via-green-500/80 to-emerald-600/80',
      shadow: isDarkMode
        ? 'shadow-[0_8px_32px_rgba(34,197,94,0.4)] group-hover/btn:shadow-[0_12px_40px_rgba(34,197,94,0.6)]'
        : 'shadow-[0_4px_20px_rgba(34,197,94,0.25)] group-hover/btn:shadow-[0_8px_32px_rgba(34,197,94,0.4)]',
      hoverColor: 'text-emerald-300',
      border: isDarkMode ? 'border-white/30' : 'border-emerald-200/50'
    },
    danger: {
      gradient: isDarkMode 
        ? 'from-red-600/60 via-red-700/60 to-red-800/60' 
        : 'from-red-500/80 via-red-600/80 to-red-700/80',
      shadow: isDarkMode
        ? 'shadow-[0_8px_32px_rgba(239,68,68,0.4)] group-hover/btn:shadow-[0_12px_40px_rgba(239,68,68,0.6)]'
        : 'shadow-[0_4px_20px_rgba(239,68,68,0.25)] group-hover/btn:shadow-[0_8px_32px_rgba(239,68,68,0.4)]',
      hoverColor: 'text-red-300',
      border: isDarkMode ? 'border-white/30' : 'border-red-200/50'
    },
    warning: {
      gradient: isDarkMode 
        ? 'from-amber-600/60 via-amber-700/60 to-amber-800/60' 
        : 'from-amber-500/80 via-amber-600/80 to-amber-700/80',
      shadow: isDarkMode
        ? 'shadow-[0_8px_32px_rgba(245,158,11,0.4)] group-hover/btn:shadow-[0_12px_40px_rgba(245,158,11,0.6)]'
        : 'shadow-[0_4px_20px_rgba(245,158,11,0.25)] group-hover/btn:shadow-[0_8px_32px_rgba(245,158,11,0.4)]',
      hoverColor: 'text-amber-300',
      border: isDarkMode ? 'border-white/30' : 'border-amber-200/50'
    },
    ghost: {
      gradient: isDarkMode 
        ? 'from-gray-900/20 via-gray-800/15 to-gray-900/20' 
        : 'from-white/60 via-blue-50/40 to-white/60',
      shadow: isDarkMode
        ? 'shadow-[0_4px_16px_rgba(0,0,0,0.2)] group-hover/btn:shadow-[0_8px_24px_rgba(0,0,0,0.3)]'
        : 'shadow-[0_2px_12px_rgba(0,0,0,0.08)] group-hover/btn:shadow-[0_4px_20px_rgba(0,0,0,0.15)]',
      hoverColor: isDarkMode ? 'text-blue-300' : 'text-blue-600',
      border: isDarkMode ? 'border-white/20' : 'border-blue-200/40'
    }
  };

  // Size styles
  const sizes = {
    small: {
      padding: 'px-3 py-2',
      iconSize: 'w-4 h-4',
      fontSize: 'text-sm'
    },
    medium: {
      padding: 'px-4 py-3',
      iconSize: 'w-5 h-5',
      fontSize: 'text-base'
    },
    large: {
      padding: 'px-6 py-4',
      iconSize: 'w-6 h-6',
      fontSize: 'text-lg'
    }
  };

  const variantStyle = variants[variant];
  const sizeStyle = sizes[size];

  // Primary nutzt exakt das CI-Blau #2563EB (echte Füllfarbe statt der generischen Klassennamen-Ersetzung)
  const primaryBackground = variant === 'primary'
    ? (isDarkMode ? 'linear-gradient(135deg, #1D4ED8, #1E3A8A)' : 'linear-gradient(135deg, #2563EB, #1D4ED8)')
    : null;

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      className={`
        relative group/btn overflow-hidden rounded-2xl transition-all duration-300 
        transform ${isDarkMode ? 'hover:scale-[1.01]' : 'hover:scale-[1.02]'} active:scale-[0.98] 
        disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
        ${variantStyle.shadow}
        ${className}
      `}
      style={{
        background: primaryBackground || `linear-gradient(135deg, ${
          variantStyle.gradient.replace('from-', '').replace(' via-', ', ').replace(' to-', ', ').replace('/60', '').replace('/70', '').replace('/80', '')
        })`,
        backdropFilter: isDarkMode ? 'blur(20px)' : 'blur(16px)',
        border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)'}`,
        boxShadow: isDarkMode 
          ? '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
          : '0 6px 24px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)'
      }}
      {...props}
    >
      {/* Enhanced Glass Overlay - Optimiert für Light Mode */}
      <div 
        className="absolute inset-0 rounded-2xl transition-all duration-350 pointer-events-none"
        style={{
          background: isDarkMode
            ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
            : 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.25) 50%, rgba(255,255,255,0.15) 100%)',
          opacity: 0,
          transform: isDarkMode ? 'translateY(0px)' : 'translateY(0px) scale(0.98)'
        }}
        ref={(el) => {
          if (el) {
            const button = el.parentElement;
            const handleMouseEnter = () => {
              if (isDarkMode) {
                el.style.opacity = '1';
                el.style.transform = 'translateY(-1px)';
              } else {
                el.style.opacity = '0.8';
                el.style.transform = 'translateY(-0.5px) scale(1.005)';
              }
            };
            const handleMouseLeave = () => {
              el.style.opacity = '0';
              el.style.transform = isDarkMode ? 'translateY(0px)' : 'translateY(0px) scale(0.98)';
            };
            button.addEventListener('mouseenter', handleMouseEnter);
            button.addEventListener('mouseleave', handleMouseLeave);
            return () => {
              button.removeEventListener('mouseenter', handleMouseEnter);
              button.removeEventListener('mouseleave', handleMouseLeave);
            };
          }
        }}
      ></div>
      
      {/* Button Content */}
      <div className={`
        relative z-10 flex items-center justify-center font-medium
        ${variant === 'primary' ? 'text-white' : (isDarkMode ? 'text-white' : 'text-black')} drop-shadow-lg
        ${sizeStyle.padding} ${sizeStyle.fontSize}
      `}>
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/80 border-t-transparent mr-2"></div>
            <span>Laden...</span>
          </>
        ) : (
          <>
            {Icon && iconPosition === 'left' && (
              <Icon className={`${sizeStyle.iconSize} mr-2 ${isDarkMode ? 'group-hover/btn:scale-105' : 'group-hover/btn:scale-[1.08]'} transition-transform duration-300`} />
            )}
            <span className="transition-transform duration-300 group-hover/btn:scale-[1.01]">{children}</span>
            {Icon && iconPosition === 'right' && (
              <Icon className={`${sizeStyle.iconSize} ml-2 ${isDarkMode ? 'group-hover/btn:scale-105' : 'group-hover/btn:scale-[1.08]'} transition-transform duration-300`} />
            )}
          </>
        )}
      </div>
      
      {/* Enhanced Wave Effect - Optimiert für beide Modi */}
      <div className={`absolute inset-0 opacity-0 ${isDarkMode ? 'group-hover/btn:opacity-20' : 'group-hover/btn:opacity-15'} transition-opacity duration-400 pointer-events-none`}>
        <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-r from-transparent via-white/20 to-transparent' : 'bg-gradient-to-r from-transparent via-white/40 to-transparent'} transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-800`}></div>
      </div>
    </button>
  );
};

export default LiquidGlassButton;